import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";

const LEGAL_DOCS = {
  privacy: {
    title: "Privacy Policy",
    updated: "Effective date: 01 July 2026",
    sections: [
      {
        heading: "",
        body: "Neural Vision Lab Pvt Ltd (\"Qelp AI\", \"we\", \"us\", \"our\") operates this website. This policy explains what information we collect through the website, how we use it, and the choices available to you.",
      },
      {
        heading: "1. Information We Collect",
        body: "When you use the contact form on this website, we collect the information you provide, which may include your name, email address, organisation, and the content of your message. We may also collect limited technical information automatically, such as your browser type, device type, and general usage patterns, through cookies and similar technologies. See our Cookie Policy for details.",
      },
      {
        heading: "2. How We Use Your Information",
        body: "We use the information collected to respond to enquiries submitted through the contact form, to understand how the website is used, and to maintain and improve the website. We do not use this information for automated decision-making.",
      },
      {
        heading: "3. Sharing of Information",
        body: "We do not sell your personal information. We may share information with service providers who help us operate this website (such as hosting or analytics providers), and only to the extent necessary for them to perform that function. We may also disclose information where required by law.",
      },
      {
        heading: "4. Data Retention",
        body: "We retain information submitted through the contact form for as long as necessary to respond to your enquiry and for a reasonable period afterward for record-keeping, unless a longer period is required by law.",
      },
      {
        heading: "5. Your Rights",
        body: "Under applicable Indian data protection law, including the Digital Personal Data Protection Act, 2023, you have the right to access, correct, or request erasure of your personal information, and to withdraw consent where consent is the basis for processing. To exercise these rights, contact us using the details below.",
      },
      {
        heading: "6. Security",
        body: "We take reasonable technical and organisational measures to protect the information we hold. No method of transmission or storage is completely secure, and we cannot guarantee absolute security.",
      },
      {
        heading: "7. Grievance Officer",
        body: "In accordance with applicable law, the following person may be contacted with any questions or grievances regarding this policy: Dr Doli Hazarika · Co-founder & Chief Scientific Officer · doli@qelpai.com · Sreenagar, Guwahati, Assam",
      },
      {
        heading: "8. Changes to This Policy",
        body: "We may update this policy from time to time. Material changes will be reflected by an updated effective date at the top of this page.",
      },
      {
        heading: "9. Contact",
        body: "For questions about this policy, contact us at qelpadmin@qelpai.com.",
      },
    ],
  },
  terms: {
    title: "Terms of Use",
    updated: "Effective date: 01 July 2026",
    sections: [
      {
        heading: "",
        body: "These terms govern your use of this website, operated by Neural Vision Lab Pvt Ltd (\"Qelp AI\", \"we\", \"us\", \"our\"). By accessing this website, you agree to these terms.",
      },
      {
        heading: "1. Purpose of This Website",
        body: "This website provides general information about Qelp AI, its research and development activities, and its team. It does not offer any product, service, or software for use, purchase, or download.",
      },
      {
        heading: "2. Stage of the Company",
        body: "Qelp AI is currently in the research and development stage. Nothing on this website should be understood as a description of a commercially available or fully developed product.",
      },
      {
        heading: "3. Intellectual Property",
        body: "All content on this website, including text, graphics, and branding, is the property of Neural Vision Lab Pvt Ltd or its licensors and is protected by applicable intellectual property law. You may not reproduce, distribute, or create derivative works from this content without our prior written permission.",
      },
      {
        heading: "4. No Warranties",
        body: "This website and its content are provided on an \"as is\" basis without warranties of any kind, whether express or implied, including as to accuracy, completeness, or fitness for a particular purpose.",
      },
      {
        heading: "5. Limitation of Liability",
        body: "To the fullest extent permitted by law, Neural Vision Lab Pvt Ltd shall not be liable for any indirect, incidental, or consequential loss arising from your use of, or inability to use, this website.",
      },
      {
        heading: "6. Third-Party Links",
        body: "This website may contain links to third-party websites. We are not responsible for the content or practices of any linked third-party website.",
      },
      {
        heading: "7. Changes to These Terms",
        body: "We may update these terms from time to time. Continued use of the website after changes are posted constitutes acceptance of the updated terms.",
      },
      {
        heading: "8. Governing Law",
        body: "These terms are governed by the laws of India. Any disputes arising from these terms shall be subject to the exclusive jurisdiction of the courts of Guwahati, Assam, India.",
      },
      {
        heading: "9. Contact",
        body: "For questions about these terms, contact us at qelpadmin@qelpai.com",
      },
    ],
  },
  cookies: {
    title: "Cookie Policy",
    updated: "Effective date: 01 July 2026",
    sections: [
      {
        heading: "",
        body: "This policy explains how Neural Vision Lab Pvt Ltd (\"Qelp AI\") uses cookies and similar technologies on this website.",
      },
      {
        heading: "1. What Are Cookies",
        body: "Cookies are small text files placed on your device when you visit a website. They help the website function properly and, where applicable, help us understand how visitors use the site.",
      },
      {
        heading: "2. Cookies We Use",
        body: "This website may use the following categories of cookies: Essential cookies, which are necessary for the website to function and cannot be switched off. Analytics cookies, which help us understand how visitors interact with the website, such as which pages are viewed and for how long.",
      },
      {
        heading: "3. Managing Cookies",
        body: "You can control or disable cookies through your browser settings. Disabling certain cookies may affect how parts of this website function.",
      },
      {
        heading: "4. Third-Party Cookies",
        body: "Where this website uses third-party services such as analytics providers, those providers may place their own cookies subject to their own privacy policies.",
      },
      {
        heading: "5. Changes to This Policy",
        body: "We may update this policy from time to time. Material changes will be reflected by an updated effective date at the top of this page.",
      },
      {
        heading: "6. Contact",
        body: "For questions about this policy, contact us at qelpadmin@qelpai.com.",
      },
    ],
  },
};

const CONSENT_KEY = "qelp_cookie_consent";

const applyConsent = (choice) => {
  if (choice === "essential") {
    window.posthog?.opt_out_capturing?.();
  } else if (choice === "accepted") {
    window.posthog?.opt_in_capturing?.();
  }
};

const Footer = () => {
  const [doc, setDoc] = useState(null);
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem(CONSENT_KEY);
    if (stored) {
      applyConsent(stored);
    } else {
      const t = setTimeout(() => setShowBanner(true), 1400);
      return () => clearTimeout(t);
    }
  }, []);

  const choose = (choice) => {
    localStorage.setItem(CONSENT_KEY, choice);
    applyConsent(choice);
    setShowBanner(false);
  };

  return (
    <footer className="py-14 px-6 md:px-16 lg:px-24 bg-[#050422] border-t border-[#10B8B1]/20" data-testid="footer">
      <div className="max-w-7xl mx-auto flex flex-col items-center gap-6 md:grid md:grid-cols-[auto_1fr_auto] md:gap-x-10 md:gap-y-4">
        <img src="/assets/qelp-logo-dark.webp" alt="Qelp AI" className="h-11 w-auto" data-testid="footer-logo" />

        <nav className="flex items-center gap-6 md:justify-self-center" data-testid="footer-legal-links">
          <Link
            to="/careers"
            className="font-mono-qelp text-[10px] tracking-[0.2em] uppercase text-[#10B8B1] hover:text-[#15cdc5] transition-colors duration-300"
            data-testid="footer-link-careers"
          >
            Careers
          </Link>
          {Object.entries(LEGAL_DOCS).map(([key, d]) => (
            <button
              key={key}
              onClick={() => setDoc(key)}
              className="font-mono-qelp text-[10px] tracking-[0.2em] uppercase text-slate-500 hover:text-[#10B8B1] transition-colors duration-300"
              data-testid={`footer-link-${key}`}
            >
              {d.title.replace(" Policy", "").replace(" of Use", "")}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-2 md:justify-self-end" data-testid="footer-status">
          <span className="w-1.5 h-1.5 rounded-full bg-[#10B8B1] animate-qelp-pulse" />
          <span className="font-mono-qelp text-[10px] tracking-[0.18em] uppercase text-slate-400">
            All Systems Operational
          </span>
        </div>

        <p className="font-mono-qelp text-[10px] tracking-[0.25em] uppercase text-slate-500">
          Measuring Cognition Before Performance
        </p>
        <span className="hidden md:block" aria-hidden="true" />
        <p className="font-mono-qelp text-[10px] tracking-[0.15em] text-slate-600 md:justify-self-end">
          © 2026 Neural Vision Lab Pvt Ltd. All Rights Reserved.
        </p>
      </div>

      <AnimatePresence>
        {showBanner && (
          <motion.div
            initial={{ opacity: 0, y: 28 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 16 }}
            transition={{ type: "spring", stiffness: 160, damping: 18 }}
            className="fixed bottom-4 left-4 sm:bottom-6 sm:left-6 lg:left-24 z-[110] w-[calc(100vw-2rem)] sm:w-[min(90vw,720px)] rounded-xl p-5 bg-[#050422] border border-[#10B8B1]/40 shadow-[0_10px_50px_rgba(5,4,34,0.85),0_0_40px_rgba(16,184,177,0.15)]"
            data-testid="cookie-banner"
          >
            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
              <span className="relative shrink-0 self-start sm:self-center">
                <svg viewBox="0 0 32 32" className="w-8 h-8" aria-hidden="true">
                  <circle cx="15" cy="17" r="10" stroke="#10B8B1" strokeWidth="1.6" fill="rgba(16,184,177,0.08)" />
                  <circle cx="24.5" cy="8" r="4.6" fill="#050422" />
                  <circle cx="24.5" cy="8" r="4.6" stroke="#10B8B1" strokeOpacity="0.25" strokeWidth="1" strokeDasharray="2 3" fill="none" />
                  <circle cx="11.5" cy="14" r="1.3" fill="#10B8B1" />
                  <circle cx="17" cy="19" r="1.3" fill="#10B8B1" />
                  <circle cx="12.5" cy="22.5" r="1.1" fill="#10B8B1" fillOpacity="0.7" />
                </svg>
                <span className="absolute -top-1 -right-1 w-1.5 h-1.5 rounded-full bg-[#10B8B1] animate-qelp-pulse" />
              </span>
              <div className="flex-1 min-w-0">
                <p className="font-heading italic text-base text-slate-100">A quick word on cookies.</p>
                <p className="mt-1 text-xs text-slate-400 leading-relaxed">
                  Essential ones keep the site running; anonymous analytics tell us how it reads. No advertising trackers. That's a promise.
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2.5 shrink-0">
                <button
                  onClick={() => choose("accepted")}
                  className="qelp-btn-primary rounded-full px-4 py-1.5 font-heading font-semibold text-xs"
                  data-testid="cookie-accept"
                >
                  Accept all
                </button>
                <button
                  onClick={() => choose("essential")}
                  className="qelp-btn-ghost rounded-full px-4 py-1.5 font-heading font-semibold text-xs"
                  data-testid="cookie-essential"
                >
                  Essential only
                </button>
                <button
                  onClick={() => { setShowBanner(false); setDoc("cookies"); }}
                  className="font-mono-qelp text-[10px] tracking-[0.15em] uppercase text-slate-500 hover:text-[#10B8B1] transition-colors duration-300 ml-1"
                  data-testid="cookie-learn-more"
                >
                  Learn more
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {doc && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-[100] flex items-center justify-center p-5 md:p-10 bg-[#050422]/85 backdrop-blur-md"
            onClick={() => setDoc(null)}
            data-testid="legal-modal"
          >
            <motion.div
              initial={{ opacity: 0, y: 32, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 24, scale: 0.98 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="qelp-card rounded-3xl max-w-2xl w-full max-h-[80vh] overflow-y-auto p-8 md:p-12 relative"
              onClick={(e) => e.stopPropagation()}
              data-testid="legal-modal-panel"
            >
              <button
                onClick={() => setDoc(null)}
                className="absolute top-5 right-5 w-9 h-9 rounded-full border border-[#10B8B1]/30 flex items-center justify-center text-slate-400 hover:text-[#10B8B1] hover:border-[#10B8B1]/60 transition-colors duration-300"
                aria-label="Close"
                data-testid="legal-modal-close"
              >
                <X size={16} />
              </button>
              <p className="font-mono-qelp text-[10px] tracking-[0.3em] uppercase text-[#10B8B1]">{LEGAL_DOCS[doc].updated}</p>
              <h3 className="mt-3 font-heading text-2xl sm:text-3xl font-medium tracking-tight text-slate-100" data-testid="legal-modal-title">
                {LEGAL_DOCS[doc].title}
              </h3>
              <div className="mt-8 space-y-7">
                {LEGAL_DOCS[doc].sections.map((s) => (
                  <div key={s.heading || "intro"}>
                    {s.heading && <h4 className="font-heading text-base sm:text-lg font-semibold text-slate-200">{s.heading}</h4>}
                    <p className="mt-2 text-sm text-slate-400 leading-relaxed">{s.body}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </footer>
  );
};

export default Footer;
